import { getClientRef, getMemberClientIds } from './ClientRepository'
import { ProjectAssignmentDetails } from '../models/ProjectAssignmentInfo'
import { db } from '../../main'
import ClientPanel from '../models/ClientPanel'
import { ClientPanelModule } from '@/store/modules/ClientPanelModule'
import {
  getClientPanelAssignmentsOfProjectAndSerialNumber, getMemberClientPanelAssignmentsOfProjectAndSerialNumber,
} from './ClientPanelAssignmentRepository'
import {
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from '@/data/models/IresDevice'
import { DevicePowerUsageType } from '../models/DeviceAttribute'
import { NewGenericDevice } from '../models/GenericDevice'
import { filterOutUndefined } from '@/utils/Utils'

export function getMemberClientPanelsRef(memberClientId: string) {
  return `clients/${memberClientId}/clientPanels`;
}

export function getClientPanelsRef() {
  return `${getClientRef()}/clientPanels`;
}

/**
 * Client panels of every member client, merged by serial number.
 *
 * Publishes a **new** Map on every snapshot. Handing out one long-lived Map and
 * mutating it in place looks equivalent and is not: assigning the same object
 * reference back into a Vue 2 data property is a no-op, and Vue cannot observe Map
 * contents either, so a consumer never saw anything past the first snapshot.
 *
 * Results are kept per member client so a panel that leaves a member's result set
 * (deleted, or no longer assigned to that client) actually disappears instead of
 * lingering for the rest of the session.
 *
 * Deliberately does not pre-fill from `ClientPanelModule`: that cache is keyed by
 * serial number alone and is only written by the single-client observers, so for a
 * master client it is either empty or holds a panel of a client outside the group.
 */
export function observeMemberClientsPanels(
  onNext: (clientPanelsMap: Map<string, ClientPanel>) => void
) {
  const clientPanelsByMemberClient: Map<string, ClientPanel[]> = new Map();
  const memberClientIds = getMemberClientIds();

  const publish = () => {
    const clientPanelsMap: Map<string, ClientPanel> = new Map();

    clientPanelsByMemberClient.forEach((clientPanels) =>
      clientPanels.forEach((clientPanel) =>
        clientPanelsMap.set(clientPanel.serialNumber, clientPanel)
      )
    );

    onNext(clientPanelsMap);
  };

  const observeCollection = (memberClientId: string) => {
    const collectionPath = getMemberClientPanelsRef(memberClientId);

    return db
      .collection(collectionPath)
      .where("flaggedAsDeleted", "==", false)
      .where("panelCurrentlyAssignedToClient", "==", true)
      .onSnapshot((snapshot) => {
        clientPanelsByMemberClient.set(
          memberClientId,
          snapshot.docs.map((doc) => doc.data() as ClientPanel)
        );

        publish();
      });
  };

  const observers = memberClientIds.map(observeCollection);

  return () => {
    observers.forEach((observer) => observer());
  };
}


export function observeClientPanels(
  onNext: (clientPanels: Array<ClientPanel>) => void
) {
  const cachedPanels = [...ClientPanelModule.clientPanelCache.values()].filter(
    (clientPanel) => clientPanel.panelCurrentlyAssignedToClient
  );

  onNext(cachedPanels);

  return db
    .collection(getClientPanelsRef())
    .where("flaggedAsDeleted", "==", false)
    .where("panelCurrentlyAssignedToClient", "==", true)
    .onSnapshot((snapshot) => {
      const clientPanels = snapshot.docs.map(
        (doc) => doc.data() as ClientPanel
      );

      clientPanels.forEach((clientPanel) =>
        ClientPanelModule.cacheClientPanel(clientPanel)
      );

      onNext(clientPanels);
    });
}

export function observeClientPanelsIncludingTemporaryAssignment(
  onNext: (clientPanels: Array<ClientPanel>) => void
) {
  const cachedPanels = [...ClientPanelModule.clientPanelCache.values()].filter(
    (clientPanel) => clientPanel.panelCurrentlyAssignedToClient ||
      clientPanel.wasAssignedToClient
  );

  onNext(cachedPanels);

  return db
    .collection(getClientPanelsRef())
    .where("flaggedAsDeleted", "==", false)
    .onSnapshot((snapshot) => {
      const clientPanels = snapshot
        .docs
        .map(
          (doc) => doc.data() as ClientPanel
        )
        .filter((clientPanel) => 
          clientPanel.panelCurrentlyAssignedToClient ||
          clientPanel.wasAssignedToClient
        );

      clientPanels.forEach((clientPanel) =>
        ClientPanelModule.cacheClientPanel(clientPanel)
      );

      onNext(clientPanels);
    });
}

export function observeClientPanelsWithCurrentProject(
  onNext: (clientPanels: Array<ClientPanel>) => void
) {
  const cachedPanels = [...ClientPanelModule.clientPanelCache.values()].filter(
    (clientPanel) =>
      clientPanel.currentProjectId && clientPanel.panelCurrentlyAssignedToClient
  );

  onNext(cachedPanels);

  return db
    .collection(getClientPanelsRef())
    .where("currentProjectId", ">=", "")
    .where("flaggedAsDeleted", "==", false)
    .where("panelCurrentlyAssignedToClient", "==", true)
    .onSnapshot((snapshot) => {
      const clientPanels = snapshot.docs.map(
        (doc) => doc.data() as ClientPanel
      );

      clientPanels.forEach((clientPanel) =>
        ClientPanelModule.cacheClientPanel(clientPanel)
      );

      onNext(clientPanels);
    });
}

export async function getClientPanelsOfProject(
  projectId: string,
  currentProject: boolean
) {
  return (
    await db
      .collection(getClientPanelsRef())
      .where(
        currentProject ? "currentProjectId" : "lastProjectId",
        "==",
        projectId
      )
      .get()
  ).docs.map((doc) => doc.data() as ClientPanel);
}

export async function getMemberClientPanel(serialNumber: string, assignedMemberClientId: string): Promise<ClientPanel | null> {
  try {
    const clientPanelRef = `${getMemberClientPanelsRef(assignedMemberClientId)}/${serialNumber}`;
    const result = await db.doc(clientPanelRef).get();
    
    return result.data() as ClientPanel;
  } catch (e) {
    return null;
  }
}

export async function getClientPanel(serialNumber: string): Promise<ClientPanel | null> {
  try {
    const clientPanelRef = `${getClientPanelsRef()}/${serialNumber}`
    const result = await db.doc(clientPanelRef).get()
    return result.data() as ClientPanel
  } catch (e) {
    return null
  }
}

export function getPowerUsageTypeFromSerialNumber(serialNumber: string) {

  const category = getIresDeviceCategoryFromSerialNumber(serialNumber)

  switch (category) {
    case IresDeviceCategory.COMBOX: {
      return DevicePowerUsageType.ELECTRICITY_METER_FOR_OTHER_DEVICES
    }
    case IresDeviceCategory.PANEL: {
      return DevicePowerUsageType.ELECTRICITY_METER
    }
  }

}

export async function saveClientPanel(clientPanel: ClientPanel) {
  const clientPanelRef = `${getClientPanelsRef()}/${clientPanel.serialNumber}`;

  return db.doc(clientPanelRef).set(clientPanel, { merge: true });
}

export async function getAllAvailablePanelsOfMemberClients() {
  const allAvailablePanels: ClientPanel[] = [];
  const memberClientIds = getMemberClientIds();

  for (const memberId of memberClientIds) {
    const memberClientPanelsRef = getMemberClientPanelsRef(memberId);

    const querySnapshot = await db
      .collection(memberClientPanelsRef)
      .where("flaggedAsDeleted", "==", false)
      .where("panelCurrentlyAssignedToClient", "==", true)
      .get();

    querySnapshot.forEach((doc) => {
      allAvailablePanels.push(doc.data() as ClientPanel);
    });
  }

  return allAvailablePanels;
}

export async function getAvailablePanels() {
  const clientPanelsRef = getClientPanelsRef();

  return (
    await db
      .collection(clientPanelsRef)
      .where("flaggedAsDeleted", "==", false)
      .where("panelCurrentlyAssignedToClient", "==", true)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanel);
}

export async function getAllClientPanels() {
  const clientPanelsRef = getClientPanelsRef();

  return (
    await db
      .collection(clientPanelsRef)
      .get()
  ).docs.map((doc) => doc.data() as ClientPanel);
}

export async function getCurrentProjectAssignmentDetails(
  serialNumber: string,
  assignedMemberClientId?: string,
): Promise<undefined | ProjectAssignmentDetails> {
  const clientPanel = assignedMemberClientId
    ? await getMemberClientPanel(serialNumber, assignedMemberClientId)
    : await getClientPanel(serialNumber);

  if (!clientPanel) {
    return undefined;
  }

  const id = clientPanel.currentProjectId;
  const name = clientPanel.currentProjectName;
  const timeRange = clientPanel.currentProjectAssignmentTimeRange;

  if (!id || !name || !timeRange) {
    return undefined;
  }

  const clientPanelAssignments = assignedMemberClientId
    ? await getMemberClientPanelAssignmentsOfProjectAndSerialNumber(
        id,
        serialNumber,
        assignedMemberClientId
      )
    : await getClientPanelAssignmentsOfProjectAndSerialNumber(id, serialNumber);

  const currentAssignment = clientPanelAssignments.find(
    (assignment) => !assignment.assignmentEndDate
  );

  return {
    assignmentId: currentAssignment?.id,
    projectId: id,
    projectName: name,
    projectNumber: clientPanel.currentProjectNumber,
    clientId: "",
    startDate: timeRange.startDate,
    endDate: timeRange.endDate,
    contactPersonNumber: clientPanel.currentProjectContactPersonNumber,
    contactPersonName: clientPanel.currentProjectContactPersonName,
  };
}

export async function updateClientPanel(
  serialNumber: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any
) {
  try {
    await db
      .collection(getClientPanelsRef())
      .doc(serialNumber)
      .set(update, { merge: true });

    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Updates multiple client panels in the Firestore collection.
 * 
 * @param updates A map where keys are serial numbers and values are update objects.
 * @returns A Promise resolving to true if all updates are successful, false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
 export async function updateClientPanelsBySerialNumber(updates: Map<string, any>): Promise<boolean> {
  try {
    // Initialize a batch
    const batch = db.batch();

    // Iterate over each entry in the updates map
    updates.forEach((update, serialNumber) => {
      const docRef = db.collection(getClientPanelsRef()).doc(serialNumber);
      batch.set(docRef, update, { merge: true });
    });

    // Commit the batch
    await batch.commit();

    return true; // All updates were successful
  } catch (error) {
    return false; // An error occurred during update
  }
}

export async function updateClientPanels(
  devicesToBeUpdated: NewGenericDevice[]
) {
  const batch = db.batch();

  for (const device of devicesToBeUpdated) {
    if (!device.serialNumber) {
      continue;
    }

    const update = filterOutUndefined({
      acquisitionDate: device.acquisitionDate,
      qrCode: device.qrCode,
      availability: device.availability,
      inventoryNumber: device.inventoryNumber,
      note: device.note,
      branch: device.branch,
      homeLocation: device.homeLocation,
      // Null when the import deletes the device, so it leaves its vehicle. Undefined
      // on every other row, and filtered out before it can clear a live assignment.
      vehicleId: device.vehicleId,
      electricityMeterNumber: device.electricityMeterNumber,
    });

    // The document id is the serial number of the panel being updated. It has to come
    // from the device, not from `update`: `update` never carries a serialNumber, so
    // `doc()` was handed undefined and threw, failing every IRES device of an import.
    const clientPanelsRef = db
      .collection(getClientPanelsRef())
      .doc(device.serialNumber);
    batch.set(clientPanelsRef, update, { merge: true });
  }

  try {
    await batch.commit();
  } catch (e) {
    // Intentionally empty
  }
}

export async function updateProjectNameOfClientPanels(
  projectId: string,
  newProjectName: string,
  newProjectNumber: string
) {
  const [current, last] = await Promise.all([
    getClientPanelsOfProject(projectId, true),
    getClientPanelsOfProject(projectId, false),
  ]);

  return Promise.all([
    current.map((clientPanel) => {
      clientPanel.currentProjectName = newProjectName;
      clientPanel.currentProjectNumber = newProjectNumber;
      return saveClientPanel(clientPanel);
    }),
    last.map((clientPanel) => {
      clientPanel.lastProjectName = newProjectName;
      clientPanel.lastProjectNumber = newProjectNumber;
      return saveClientPanel(clientPanel);
    }),
  ]);
}

export async function findPanelsWithInventoryNumber(
  inventoryNumber: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  try {
    const querySnapshot = await db
      .collection(getClientPanelsRef())
      .where("inventoryNumber", "==", inventoryNumber)
      .get();

    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    return [];
  }
}

export async function findPanelsWithQrCode(
  qrCode: string
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
  try {
    const querySnapshot = await db
      .collection(getClientPanelsRef())
      .where("qrCode", "==", qrCode)
      .get();

    return querySnapshot.docs.map((doc) => doc.data());
  } catch (error) {
    return [];
  }
}

/**
 * Updates the branch for all client panels where `brancg` matches matches the 
 * specified `oldBranchNumber`.
 * 
 * @param oldBranchNumber - The branch number to search for in client panels' `branch`.
 * @param newBranchNumber - The new branch number to set in the matched client panels' `branch`.
 * 
 * @returns A promise that resolves when all client panels have been updated.
 */
export async function updateClientPanelsBranch(
  oldBranchNumber: string,
  newBranchNumber: string
) {
  const clientPanelsRef = db.collection(getClientPanelsRef());
  
  const querySnapshot = await clientPanelsRef
    .where('branch', '==', oldBranchNumber)
    .get();

  const panelsToUpdate = querySnapshot.docs;

  // Split the documents into batches of 500
  const BATCH_SIZE = 500;
  const batches = [];
  for (let i = 0; i < panelsToUpdate.length; i += BATCH_SIZE) {
    batches.push(panelsToUpdate.slice(i, i + BATCH_SIZE));
  }

  // Update documents in batches
  for (const batchDocs of batches) {
    const batch = db.batch();
    batchDocs.forEach((doc) => {
      batch.update(doc.ref, { branch: newBranchNumber });
    });
    await batch.commit();
  }
}